/**
 * A class responsible for managing a marquee effect for testimonials.
 * This class handles resizing, animation timing, and ensuring the marquee content
 * dynamically adapts to the size of its elements.
 */
class TestimonialsMarquee {

	/**
	 * @return {void}
	 */
	static init() {

		const roots = document.querySelectorAll( '.b-testimonials' );
		for ( const el of roots ) {

			new TestimonialsMarquee( el );

		}

	}

	/**
	 * @param {HTMLElement} element Block root.
	 */
	constructor( element ) {

		this.resizeTimeout = null;
		this.stripRoTimer = null;
		this.stripObserver = null;
		this.lastStripWidth = null;
		this.rafId = null;
		this.lastTs = 0;
		this.offsetPx = 0;
		this.speedPxPerSec = 0;
		this.itemGapPx = 0;
		this.isPaused = false;
		this.root = element;
		this.scrollEl = element.querySelector( '.js-testimonials-scroll' );
		this.track = null;
		this.strip = null;
		this.stepLoop = this.stepLoop.bind( this );
		window.addEventListener( 'resize', this.onWindowResize.bind( this ) );
		if ( this.scrollEl ) {

			this.scrollEl.addEventListener( 'mouseenter', () => {

				this.isPaused = true;

			} );
			this.scrollEl.addEventListener( 'mouseleave', () => {

				this.isPaused = false;

			} );

		}
		this.update();

	}

	/**
	 * @return {void}
	 */
	disconnectStripObserver() {

		if ( this.stripObserver ) {

			this.stripObserver.disconnect();
			this.stripObserver = null;

		}
		if ( this.stripRoTimer !== null ) {

			clearTimeout( this.stripRoTimer );
			this.stripRoTimer = null;

		}

	}

	/**
	 * @return {void}
	 */
	attachStripResizeObserver() {

		if ( typeof ResizeObserver === 'undefined' || ! this.scrollEl ) {

			return;

		}
		const strip = this.scrollEl.querySelector( '.js-testimonials-marquee-item' );
		if ( ! strip ) {

			return;

		}
		this.disconnectStripObserver();
		this.stripObserver = new ResizeObserver( () => {

			const stripEl = this.scrollEl.querySelector( '.js-testimonials-marquee-item' );
			if ( ! stripEl ) {

				return;

			}
			const w = stripEl.getBoundingClientRect().width;
			if (
				this.lastStripWidth !== null &&
				Math.abs( w - this.lastStripWidth ) <= 1
			) {

				return;

			}
			if ( this.stripRoTimer !== null ) {

				clearTimeout( this.stripRoTimer );

			}
			this.stripRoTimer = window.setTimeout( () => {

				this.stripRoTimer = null;
				this.update();

			}, 120 );

		} );
		this.stripObserver.observe( strip );

	}

	/**
	 * @return {void}
	 */
	onWindowResize() {

		clearTimeout( this.resizeTimeout );
		this.resizeTimeout = setTimeout( this.update.bind( this ), 250 );

	}

	/**
	 * @return {void}
	 */
	stopLoop() {

		if ( this.rafId !== null ) {

			cancelAnimationFrame( this.rafId );
			this.rafId = null;

		}
		this.lastTs = 0;

	}

	/**
	 * @return {void}
	 */
	startLoop() {

		this.stopLoop();
		this.rafId = requestAnimationFrame( this.stepLoop );

	}

	/**
	 * @param {number} ts Animation frame timestamp.
	 * @return {void}
	 */
	stepLoop( ts ) {

		if ( ! this.strip ) {

			return;

		}
		if ( this.lastTs <= 0 ) {

			this.lastTs = ts;

		}

		const deltaSec = ( ts - this.lastTs ) / 1000;
		this.lastTs = ts;

		if ( ! this.isPaused ) {

			this.offsetPx += this.speedPxPerSec * deltaSec;
			let first = this.strip.firstElementChild;
			while ( first ) {

				const firstWidth = first.getBoundingClientRect().width + this.itemGapPx;
				if ( this.offsetPx < firstWidth ) {

					break;

				}
				this.offsetPx -= firstWidth;
				this.strip.appendChild( first );
				first = this.strip.firstElementChild;

			}
			this.strip.style.transform = `translate3d(${ -this.offsetPx }px, 0, 0)`;

		}

		this.rafId = requestAnimationFrame( this.stepLoop );

	}

	/**
	 * @param {HTMLElement} root Block root.
	 * @return {{ baseSec: number, refPx: number }}
	 */
	static readMarqueeTimingFromRoot( root ) {

		const fromDataSec = parseFloat( root.dataset.marqueeBaseSec || '' );
		const fromDataRef = parseFloat( root.dataset.marqueeRefPx || '' );
		const baseSec = Number.isFinite( fromDataSec )
			? fromDataSec
			: parseFloat( getComputedStyle( root ).getPropertyValue( '--animation-speed' ).trim() ) || 40;
		const refPx = Number.isFinite( fromDataRef )
			? fromDataRef
			: parseFloat( getComputedStyle( root ).getPropertyValue( '--marquee-ref-shift-px' ).trim() ) || 1000;

		const refSafe = refPx > 0 ? refPx : 1000;

		return { baseSec, refPx: refSafe };

	}

	/**
	 * @return {void}
	 */
	update() {

		this.disconnectStripObserver();
		this.stopLoop();

		if ( ! this.scrollEl ) {

			return;

		}
		const track = this.scrollEl.querySelector( '.js-testimonials-marquee-track' );
		const strip = this.scrollEl.querySelector( '.js-testimonials-marquee-item' );
		if ( ! track || ! strip ) {

			return;

		}

		const clones = track.querySelectorAll( '.js-testimonials-marquee-item.clone' );
		for ( const c of clones ) {

			c.parentNode.removeChild( c );

		}
		const fills = strip.querySelectorAll( '.js-testimonials-marquee-fill' );
		for ( const fill of fills ) {

			fill.parentNode.removeChild( fill );

		}
		track.classList.remove( 'b-testimonials__marquee-track--play' );
		this.track = track;
		this.strip = strip;
		this.offsetPx = 0;
		this.strip.style.transform = 'translate3d(0, 0, 0)';

		const cardsCount = this.strip.children.length;
		if ( cardsCount < 2 ) {

			return;

		}

		const viewportWidth = this.scrollEl.getBoundingClientRect().width;
		const baseCards = Array.from( this.strip.children );
		let stripWidth = this.strip.getBoundingClientRect().width;
		let fillIndex = 0;
		let fillSafety = 0;
		const minStripWidth = viewportWidth * 2;
		while ( stripWidth < minStripWidth && baseCards.length > 0 && fillSafety < 300 ) {

			const fillCard = baseCards[ fillIndex % baseCards.length ].cloneNode( true );
			fillCard.classList.add( 'js-testimonials-marquee-fill' );
			fillCard.setAttribute( 'aria-hidden', 'true' );
			this.strip.appendChild( fillCard );
			fillIndex += 1;
			fillSafety += 1;
			stripWidth = this.strip.getBoundingClientRect().width;

		}

		const { baseSec, refPx } = TestimonialsMarquee.readMarqueeTimingFromRoot( this.root );
		this.speedPxPerSec = Math.max( 1, refPx / Math.max( 1, baseSec ) );
		const stripStyle = getComputedStyle( this.strip );
		this.itemGapPx = parseFloat( stripStyle.columnGap || stripStyle.gap || '0' ) || 0;
		this.lastStripWidth = strip.getBoundingClientRect().width;
		this.startLoop();
		this.attachStripResizeObserver();

	}

}

document.addEventListener( 'DOMContentLoaded', TestimonialsMarquee.init );
